// Feature-detection + environment helpers for web push. Kept dependency-free so
// both the hook and the settings UI can call it to decide what to render.

export interface PushSupport {
  // The Push API, service workers, and the Notification API are all present.
  supported: boolean
  // Current browser permission for notifications.
  permission: NotificationPermission
  // Whether the app is running as an installed/standalone PWA. On iOS, push only
  // works from a Home-Screen install (iOS 16.4+), so the UI uses this to show an
  // "Add to Home Screen" hint instead of a permission prompt.
  standalone: boolean
  // iOS Safari (incl. iPad reporting as desktop). Combined with `standalone`,
  // this drives the iOS-specific messaging.
  isIOS: boolean
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS exposes navigator.standalone; everyone else uses the display-mode media query.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  return iosStandalone || displayModeStandalone
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  // iPhone/iPod, plus iPadOS which masquerades as Mac but reports touch points.
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

export function getPushSupport(): PushSupport {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

  return {
    supported,
    permission: supported ? Notification.permission : 'denied',
    standalone: detectStandalone(),
    isIOS: detectIOS()
  }
}
